/**
 * ThemeContext.js — Global dark/light theme provider for Sixers Performance app.
 *
 * Usage in any component:
 *   import { useTheme, getT } from "./ThemeContext";
 *   ...
 *   const { isDark } = useTheme();
 *   const T = getT(isDark);   // replaces the static T object
 *
 * The toggle lives in LandingPage header. Preference is stored in localStorage.
 */
import React, { createContext, useContext, useState, useEffect } from "react";
import API_BASE from "./apiConfig";
import { buildButtonTokens, buildStatusPill } from "./utils/themeStyles";

const ThemeContext = createContext({ isDark: true, toggleTheme: () => {}, refreshBrandColors: () => {} });

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      const s = localStorage.getItem("sixers_theme");
      return s ? s === "dark" : false; // default: light
    } catch { return false; }
  });
  const [, bumpBrand] = useState(0);

  // Fetch the tenant's brand identity (org name/colors, from app_config) and apply
  // it to the token sets below — this is what makes the app's color scheme
  // configurable per business instead of hardcoded. Runs once at startup, and again
  // any time refreshBrandColors() is called (e.g. right after Config Master saves),
  // so a color change reflects app-wide without requiring a page reload.
  const refreshBrandColors = () => {
    return fetch(`${API_BASE}/config/`)
      .then(r => r.json())
      .then(d => {
        const cfg = d?.config || {};
        setBrandColors({
          blue: cfg.primary_color, blueDark: cfg.accent_color, yellow: cfg.secondary_color,
          green: cfg.success_color, red: cfg.danger_color, orange: cfg.warning_color,
          buttonStyle: cfg.button_status_style,
        });
        bumpBrand(v => v + 1); // force consumers to re-render with the new tokens
      })
      .catch(() => {});
  };

  useEffect(() => { refreshBrandColors(); }, []);

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem("sixers_theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, refreshBrandColors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// ─── Token sets ───────────────────────────────────────────────────────────────
// Brand colors — same in both themes. Defaults match the historical Sixers palette;
// setBrandColors() (called by ThemeProvider from GET /config/) overrides them per
// tenant, so a different business can rebrand the whole app by editing app_config
// instead of touching code.
const BRAND = {
  blue:       "#034e7b",
  blueDark:   "#02304f",
  yellow:     "#f7ce0b",
  green:      "#059669",
  red:        "#dc2626",
  orange:     "#d97706",
  font:       "'Segoe UI', system-ui, sans-serif",
  blur:       "blur(12px)",
  buttonStyle: "solid",
};

function _darken(hex, amt = 20) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    const r = Math.max(0, (n >> 16) - amt), g = Math.max(0, ((n >> 8) & 0xff) - amt), b = Math.max(0, (n & 0xff) - amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch { return hex; }
}

function _lighten(hex, amt = 40) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, (n >> 16) + amt), g = Math.min(255, ((n >> 8) & 0xff) + amt), b = Math.min(255, (n & 0xff) + amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch { return hex; }
}

function _hexToRgb(hex) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    return `${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff}`;
  } catch { return "0,0,0"; }
}

export function setBrandColors({ blue, blueDark, yellow, green, red, orange, buttonStyle } = {}) {
  if (blue)       BRAND.blue       = blue;
  if (blueDark)   BRAND.blueDark   = blueDark;
  if (yellow)     BRAND.yellow     = yellow;
  if (green)      BRAND.green      = green;
  if (red)        BRAND.red        = red;
  if (orange)     BRAND.orange     = orange;
  if (buttonStyle) BRAND.buttonStyle = buttonStyle;
}

// Live read access for other brand-token modules (branding.js) so there is one
// underlying source of truth even where a second import path still exists.
export function getBrandColors() {
  return { ...BRAND };
}

// Exported so other files building their own gradients/shades from the live
// brand colors (e.g. StudentMaster.js's ST.yellowGrad) don't need their own copy.
export const darken = _darken;
export const lighten = _lighten;

function _yellowBtn() {
  return { background: `linear-gradient(135deg,${BRAND.yellow},${_darken(BRAND.yellow)})`, color: BRAND.blueDark, fontWeight: 800, border: "none" };
}
function _tabActiveBgDark() { return `linear-gradient(135deg,${BRAND.yellow},${_darken(BRAND.yellow)})`; }

function buildDark() { return {
  ...BRAND,
  isDark:       true,
  colorScheme:  "dark",
  pageBg:       "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)",
  glass:        "rgba(255,255,255,0.06)",
  glassBdr:     "1px solid rgba(255,255,255,0.10)",
  glassSh:      "0 4px 28px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.06)",
  textPrimary:  "rgba(255,255,255,0.92)",
  textSub:      "rgba(255,255,255,0.70)",
  textLabel:    "rgba(255,255,255,0.55)",
  textMuted:    "rgba(255,255,255,0.45)",
  gray:         "rgba(255,255,255,0.45)",
  inputBg:      "rgba(255,255,255,0.07)",
  inputBdr:     "rgba(255,255,255,0.10)",
  inputText:    "rgba(255,255,255,0.88)",
  get tabActiveBg()  { return _tabActiveBgDark(); },
  get tabActiveClr() { return BRAND.blueDark; },
  tabInBg:      "rgba(255,255,255,0.06)",
  tabInClr:     "rgba(255,255,255,0.55)",
  // Status colors — configurable (app_config success/danger/warning_color);
  // lightened for visibility against the dark background.
  get green()    { return _lighten(BRAND.green); },
  get red()      { return _lighten(BRAND.red); },
  get orange()   { return _lighten(BRAND.orange); },
  get greenBg()  { return `rgba(${_hexToRgb(BRAND.green)},0.12)`; },
  get redBg()    { return `rgba(${_hexToRgb(BRAND.red)},0.12)`; },
  get orangeBg() { return `rgba(${_hexToRgb(BRAND.orange)},0.12)`; },
  // Chart Tokens
  chartBg:      "linear-gradient(160deg, #060d1f 0%, #0a1832 60%, #071428 100%)",
  chartBdr:     "1px solid rgba(255,255,255,0.08)",
  chartGrid:    "rgba(255,255,255,0.04)",
  chartText:    "rgba(255,255,255,0.38)",
  divider:      "rgba(255,255,255,0.08)",
  dropdownBg:   "rgba(8,20,40,0.96)",
  dropdownBdr:  "1px solid rgba(255,255,255,0.10)",
  dropdownItem: "rgba(255,255,255,0.88)",
  get yellowBtn() { return _yellowBtn(); },
  // Accent text: yellow in dark (readable on dark bg), dark-amber in light (readable on white)
  get accentText() { return BRAND.yellow; },
  // Button/status theme — see utils/themeStyles.js. Driven by app_config's
  // button_status_style (solid/soft/outline) plus the same brand colors
  // above, so switching the style in Config Master reflects everywhere
  // that consumes these tokens instead of a local hardcoded style.
  get btnPrimary() { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: true, style: BRAND.buttonStyle }).primary; },
  get btnGhost()   { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: true, style: BRAND.buttonStyle }).ghost; },
  get btnDanger()  { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: true, style: BRAND.buttonStyle }).danger; },
  statusPill(tone) { return buildStatusPill({ tone, green: BRAND.green, red: BRAND.red, orange: BRAND.orange, blue: BRAND.blue, isDark: true, style: BRAND.buttonStyle }); },
}; }

function buildLight() { return {
  ...BRAND,
  isDark:       false,
  colorScheme:  "light",
  pageBg:       "#f0f6ff",
  glass:        "rgba(255,255,255,0.95)",
  glassBdr:     "1px solid rgba(3,78,123,0.18)",
  glassSh:      "0 4px 20px rgba(0,40,100,0.08)",
  get textPrimary() { return BRAND.blueDark; },
  textSub:      "#1a3a5a",
  textLabel:    "#3a5a7a",
  textMuted:    "#5a7a9a",
  gray:         "#8899aa",
  inputBg:      "#ffffff",
  inputBdr:     "rgba(3,78,123,0.20)",
  inputText:    "#021d35",
  get tabActiveBg()  { return BRAND.yellow; },
  get tabActiveClr() { return BRAND.blueDark; },
  tabInBg:      "rgba(3,78,123,0.06)",
  tabInClr:     "#3a5a7a",
  // Status colors — configurable (app_config success/danger/warning_color).
  get green()    { return BRAND.green; },
  get red()      { return BRAND.red; },
  get orange()   { return BRAND.orange; },
  get greenBg()  { return `rgba(${_hexToRgb(BRAND.green)},0.08)`; },
  get redBg()    { return `rgba(${_hexToRgb(BRAND.red)},0.08)`; },
  get orangeBg() { return `rgba(${_hexToRgb(BRAND.orange)},0.08)`; },
  // Chart Tokens
  chartBg:      "#ffffff",
  chartBdr:     "1px solid rgba(3,78,123,0.15)",
  chartGrid:    "rgba(3,78,123,0.08)",
  chartText:    "rgba(3,78,123,0.55)",
  divider:      "rgba(3,78,123,0.12)",
  dropdownBg:   "#ffffff",
  dropdownBdr:  "1px solid rgba(3,78,123,0.15)",
  get dropdownItem() { return BRAND.blueDark; },
  get yellowBtn() { return _yellowBtn(); },
  // Dark amber — readable on white backgrounds in light mode
  accentText:   "#92400e",
  get btnPrimary() { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: false, style: BRAND.buttonStyle }).primary; },
  get btnGhost()   { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: false, style: BRAND.buttonStyle }).ghost; },
  get btnDanger()  { return buildButtonTokens({ yellow: BRAND.yellow, blueDark: BRAND.blueDark, red: BRAND.red, green: BRAND.green, orange: BRAND.orange, blue: BRAND.blue, isDark: false, style: BRAND.buttonStyle }).danger; },
  statusPill(tone) { return buildStatusPill({ tone, green: BRAND.green, red: BRAND.red, orange: BRAND.orange, blue: BRAND.blue, isDark: false, style: BRAND.buttonStyle }); },
}; }

/**
 * Returns the full theme token set for the given mode.
 * Call this inside your component function (not at module level) to get reactive tokens.
 * Rebuilt on every call so brand-color changes (from GET /config/) show up immediately.
 *
 * @param {boolean} isDark
 * @returns {object} theme tokens
 */
export function getT(isDark) {
  return isDark ? buildDark() : buildLight();
}

/**
 * Glass card style shorthand — use as spread: `style={{ ...glassCard(T) }}`
 */
export function glassCard(T) {
  return {
    background:          T.glass,
    backdropFilter:      T.blur, WebkitBackdropFilter: T.blur,
    WebkitBackdropFilter:T.blur,
    border:              T.glassBdr,
    borderRadius:        16,
    boxShadow:           T.glassSh,
  };
}
